import React from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LazyImage from "@insite/mobius/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import Link from "@insite/mobius/Link";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import getColor from "@insite/mobius/utilities/getColor";

const enum fields {
    title = "title",
    acrowWhatWeDoItem = "acrowWhatWeDoItem",
    buttonText = "buttonText",
    link = "link",
}

export interface AcrowWhatWeDoModel {
    fields: {
        image: string;
        caption: string;
        content: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.buttonText]: string;
        [fields.link]: LinkFieldValue;
        [fields.acrowWhatWeDoItem]: AcrowWhatWeDoModel[];
    };
}

export interface AcrowWhatWeDoStyles {
    mainContainer?: InjectableCss;
    titleText?: TypographyProps;
    infoGridItem?: GridItemProps;
    container?: InjectableCss;
    boxContainer?: InjectableCss;
    stepContainer?: InjectableCss;
    stepTextContainer?: InjectableCss;
    stepText?: TypographyProps;
    caption?: TypographyProps;
    info?: TypographyProps;
    imageContainer?: InjectableCss;
    contentContainer?: InjectableCss;
    button?: InjectableCss;
}

const acrowWhatWeDoStyle: AcrowWhatWeDoStyles = {
    mainContainer: {
        css: css`
            color: ${getColor("text.main")};
        `,
    },
    titleText: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 24px;
            font-weight: 500;
            line-height: 32px;
            letter-spacing: 0;
            text-align: center;
            color: ${getColor("text.main")};
            text-transform: uppercase;
            padding-bottom: 25px;
            margin: 0;
            margin-bottom: 5px;
        `,
    },
    infoGridItem: {
        css: css`
            padding: 8px;
        `,
    },
    container: {
        css: css`
            padding: 16px;
            position: relative;
        `,
    },
    boxContainer: {
        css: css`
            box-sizing: border-box;
            text-align: center;
            padding: 0 8px;
            transition: 0.2s all ease;
            overflow: hidden;
            padding-top: 20px;
        `,
    },
    stepContainer: {
        css: css`
            position: absolute;
            display: block;
            width: 58px;
            height: 58px;
            position: absolute;
            background:  ${getColor("primary.contrast")};
            top: 0;
            left: 0;
        `,
    },
    stepTextContainer: {
        css: css`
            display: block;
            width: 40px;
            height: 40px;
            position: absolute;
            background: ${getColor("text.main")} !important;
            bottom: -15px;
            left: 46%;
            border-radius: 21px;
        `,
    },
    stepText: {
        css: css`
            color: ${getColor("primary.contrast")};
            display: flex;
            justify-content: center;
            align-items: center;
            width: 40px;
            height: 40px;
            font-size: 24px;
            font-weight: normal;
        `,
    },
    caption: {
        css: css`
            font-size: 16px;
            font-weight: 500;
            line-height: 28px;
            letter-spacing: 0;
            text-align: center;
            margin: 1rem 0;
        `,
    },

    info: {
        css: css`
            font-size: 13px;
            font-weight: 400;
            line-height: 19px;
            letter-spacing: 0;
            text-align: center;
            margin-bottom: 0;
        `,
    },

    imageContainer: {
        css: css`
            margin-left: 10%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                ])}
        `,
    },
    button: {
        css: css`
            margin-top: 20px;
            color: ${getColor("primary.contrast")} !important;
            transition: 0.3s;
            max-width: 100%;
            word-wrap: break-word;
            background: ${getColor("primary.main")};
            border: 1px solid ${getColor("primary.main")};
            color: ${getColor("text.main")} !important;
            cursor: pointer;
            display: inline-block;
            font-weight: 500;
            box-sizing: border-box;
            vertical-align: middle;
            padding: 14px 17px;
            border-radius: 0px;
            box-shadow: none;
            ${TypographyStyle} {
                color: ${getColor("primary.contrast")};
                font-style: normal;
                font-weight: bold;
                line-height: 1.42857143;
                font-size: 21px;
                line-height: 27px;
            }
            &:hover {
                background: ${getColor("primary.contrast")};
                ${TypographyStyle} {
                    color: ${getColor("primary.main")};
                }
            }
        `,
    },
    contentContainer: {
        css: css`
            position: relative;
        `,
    },
};

const styles = acrowWhatWeDoStyle;

const AcrowWhatWeDo = ({ fields }: OwnProps) => {
    const link = useGetLink(fields.link);
    return (
        <StyledWrapper {...styles.mainContainer}>
            <Typography {...styles.titleText}>{fields.title}</Typography>
            <GridContainer>
                {fields.acrowWhatWeDoItem?.length > 0 &&
                    fields.acrowWhatWeDoItem.map((item, index) => (
                    <GridItem width={[12, 12, 4, 4, 4]} {...styles.infoGridItem} key={item.fields.caption || item.fields.image}>
                        <StyledWrapper {...styles.container}>
                            <StyledWrapper {...styles.boxContainer}>
                                <StyledWrapper>
                                    <StyledWrapper {...styles.contentContainer}>
                                        <LazyImage src={item.fields.image}></LazyImage>

                                        <StyledWrapper {...styles.stepTextContainer}>
                                            <Typography as="h3" {...styles.stepText}>
                                                {index + 1}
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>

                                    <Typography as="div" {...styles.caption}>
                                        {item.fields.caption}
                                    </Typography>
                                    <Typography variant="p" {...styles.info}>
                                        {item.fields.content}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                ))}
                <GridItem
                    width={12}
                    {...styles.infoGridItem}
                    style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}
                >
                    <StyledWrapper>
                        <Link href={link.url} {...styles.button}>
                            {fields.buttonText}
                        </Link>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const AcrowWhatWeDoModule: WidgetModule = {
    component: AcrowWhatWeDo,
    definition: {
        displayName: "Acrow what We Do",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "link",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.acrowWhatWeDoItem,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.caption;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Icon Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },

                    {
                        name: "caption",
                        displayName: "Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "content",
                        displayName: "Content",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default AcrowWhatWeDoModule;
